import { useState } from 'react';
import api from '../api';

const t = {
  ground: '#0e0a08',
  wing: '#120c09',
  house: '#1a120d',
  edge: 'rgba(237, 228, 211, 0.08)',
  accent: '#e8b547',
  textHi: '#ede4d3',
  textMid: 'rgba(237, 228, 211, 0.62)',
  textLow: 'rgba(237, 228, 211, 0.38)',
  danger: '#d47e6a',
  warn: '#e8b547',
  suspend: '#d4915c',
  fontBody: '"Geist", "Inter", system-ui, sans-serif',
};

export default function ModActions({ post, reply, onAction }) {
  const [showMenu, setShowMenu] = useState(false);
  const [showReasonInput, setShowReasonInput] = useState(null);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const targetUserId = reply ? reply.author : post.author;
  const targetAlias = reply ? reply.authorAlias : post.authorAlias;

  const doAction = async () => {
    if (!reason.trim()) return;
    setLoading(true);
    try {
      switch (showReasonInput) {
        case 'remove':
          if (reply) {
            await api.post(`/mod/posts/${post._id}/replies/${reply._id}/remove`, { reason });
          } else {
            await api.post(`/mod/posts/${post._id}/remove`, { reason });
          }
          break;
        case 'lock':
          await api.post(`/mod/posts/${post._id}/lock`, { reason });
          break;
        case 'warn':
          await api.post(`/mod/users/${targetUserId}/warn`, { reason });
          break;
        case 'suspend24':
          await api.post(`/mod/users/${targetUserId}/suspend`, { reason, hours: 24 });
          break;
        case 'suspend72':
          await api.post(`/mod/users/${targetUserId}/suspend`, { reason, hours: 72 });
          break;
        case 'ban':
          await api.post(`/mod/users/${targetUserId}/ban-request`, { reason });
          break;
        case 'flag':
          await api.post('/mod/flag', { reason, postId: post._id, userId: targetUserId });
          break;
        default:
          break;
      }
      setShowMenu(false);
      setShowReasonInput(null);
      setReason('');
      onAction?.();
    } catch (err) {
      alert(err.response?.data?.error || 'Action failed');
    }
    setLoading(false);
  };

  const actionLabels = {
    remove: reply ? 'Remove reply' : 'Remove post',
    lock: post.locked ? 'Unlock thread' : 'Lock thread',
    warn: `Warn ${targetAlias}`,
    suspend24: `Suspend ${targetAlias} (24h)`,
    suspend72: `Suspend ${targetAlias} (72h)`,
    ban: `Request ban for ${targetAlias}`,
    flag: 'Flag for admin'
  };

  if (showReasonInput) {
    return (
      <div style={{
        background: t.ground, border: `1px solid ${t.edge}`,
        padding: 12, marginTop: 8,
      }}>
        <p style={{ fontFamily: t.fontBody, fontSize: 12, color: t.textLow, margin: '0 0 8px' }}>
          {actionLabels[showReasonInput]}
        </p>
        <input
          type="text"
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="Reason (required)..."
          autoFocus
          style={{
            display: 'block', width: '100%', boxSizing: 'border-box',
            background: t.wing, border: `1px solid ${t.edge}`,
            padding: '8px 12px', color: t.textHi, fontFamily: t.fontBody, fontSize: 14,
            outline: 'none',
          }}
        />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <button onClick={() => { setShowReasonInput(null); setReason(''); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.textLow, fontSize: 12, fontFamily: t.fontBody }}>
            Cancel
          </button>
          <button onClick={doAction} disabled={loading || !reason.trim()}
            style={{
              background: t.accent, color: t.ground, fontFamily: t.fontBody, fontWeight: 600,
              fontSize: 12, padding: '4px 12px', border: 'none', cursor: 'pointer',
              opacity: loading || !reason.trim() ? 0.5 : 1,
            }}>
            {loading ? '...' : 'Confirm'}
          </button>
        </div>
      </div>
    );
  }

  const menuItem = (label, color, onClick) => (
    <button onClick={onClick} style={{
      display: 'block', width: '100%', textAlign: 'left',
      padding: '8px 16px', fontSize: 12, color, fontFamily: t.fontBody,
      background: 'transparent', border: 'none', cursor: 'pointer',
    }}
      onMouseEnter={e => e.currentTarget.style.background = t.ground}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >{label}</button>
  );

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button onClick={() => setShowMenu(!showMenu)}
        style={{
          color: t.textLow, fontSize: 12, background: 'transparent',
          border: 'none', cursor: 'pointer', fontFamily: t.fontBody, padding: '0 4px',
          opacity: 0.5,
        }}
        onMouseEnter={e => { e.currentTarget.style.color = t.accent; e.currentTarget.style.opacity = 1; }}
        onMouseLeave={e => { e.currentTarget.style.color = t.textLow; e.currentTarget.style.opacity = 0.5; }}
      >mod</button>
      {showMenu && (
        <div style={{
          position: 'absolute', right: 0, top: '100%', marginTop: 4,
          background: t.wing, border: `1px solid ${t.edge}`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)', zIndex: 10,
          padding: '4px 0', minWidth: 180,
        }}>
          {!reply && (
            <>
              {menuItem('Remove post', t.textHi, () => { setShowReasonInput('remove'); setShowMenu(false); })}
              {menuItem(post.locked ? 'Unlock thread' : 'Lock thread', t.textHi, () => { setShowReasonInput('lock'); setShowMenu(false); })}
            </>
          )}
          {reply && menuItem('Remove reply', t.textHi, () => { setShowReasonInput('remove'); setShowMenu(false); })}
          <div style={{ height: 1, background: t.edge, margin: '4px 0' }} />
          {menuItem(`Warn ${targetAlias}`, t.warn, () => { setShowReasonInput('warn'); setShowMenu(false); })}
          {menuItem('Suspend 24h', t.suspend, () => { setShowReasonInput('suspend24'); setShowMenu(false); })}
          {menuItem('Suspend 72h', t.suspend, () => { setShowReasonInput('suspend72'); setShowMenu(false); })}
          {menuItem('Request ban', t.danger, () => { setShowReasonInput('ban'); setShowMenu(false); })}
          <div style={{ height: 1, background: t.edge, margin: '4px 0' }} />
          {menuItem('Flag for admin', t.textLow, () => { setShowReasonInput('flag'); setShowMenu(false); })}
        </div>
      )}
    </div>
  );
}
